import React, { createContext, useContext, useEffect, useRef } from "react";
import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import BookingTypeTime from "./bookingTypeTime";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "react-color-palette/lib/css/styles.css";
import "react-phone-number-input/style.css";
import { UserContext } from "../../auth/auth-context";
import { useDispatch } from "react-redux";
import ConfirmDialog from './confirmDialog';
import { useCloseConfirmation } from "./useCloseConfirmation";
import { DELETE_INVITEE_QUESTION } from "../../../graphql/mutations";
import { useSelectedBusiness } from "../../../redux/store";
import { ADD_BOOKING_TYPE, UPDATE_BOOKING_TYPE } from "../../../graphql/mutations/bookingType";
import { updateBookingType } from "../../../redux/actions/businessActions";
import { GET_BUSINESS_BOOKING_TYPES } from "../../../graphql/queries";
import { usePermissions } from "../../../hooks/usePermissions";

const bookingCategoryMap = {
    event: 1,
    service: 2,
    class: 3,
};
 

const getBookingCategory = (input) => {
    if (typeof input === "string") {
        return bookingCategoryMap[input.toLowerCase()] !== undefined
            ? input.toLowerCase()
            : "";
    } else if (typeof input === "number") {
        for (const category in bookingCategoryMap) {
            if (bookingCategoryMap[category] === input) {
                return category;
            }
        }
        return "";
    } else {
        return "";
    }
};


// Create context
const BookingTypeContext = createContext();

// Create a custom hook to use the context
export const useBookingTypeContext = () => useContext(BookingTypeContext);
function BookingTypeProvider(allProps) {
    const selectedBusiness = useSelectedBusiness();
    const { children, ...props } = allProps;
    const location = useLocation(); // Get full URL path 
    const isUpdateMode = location.pathname.includes("/update/");

    const {
        CancelConfirmationModal,
    } = useCloseConfirmation(props);

    const {
        loading,
        error,
        data: businessBookingTypes,
        refetch,
    } = useQuery(GET_BUSINESS_BOOKING_TYPES, {
        variables: {
            businessUrl: selectedBusiness?.businessBasic.url
        },
    });



    const [deleteInviteeQuestion] = useMutation(DELETE_INVITEE_QUESTION, {
        onCompleted: () => {
            window.location.reload(); // Hard refresh of the page after successful deletion
        },
    });

    const navigate = useNavigate();

    const [isDisabled, setIsDisabled] = useState(false);
    const [errors, setErrors] = useState({});

    const userContext = useContext(UserContext);
    const providerUser = userContext.providerUser;
    const dispatch = useDispatch();

    const { id, bookingtypecategory } = useParams();

    const [bookingType, setBookingType] = useState(null); 
 

    const [isScheduleSettings, setIsScheduleSettings] = useState(false);
    const [showInviteeQuestions, setShowInviteeQuestions] = useState(false);

    const [showSection, setShowSection] = useState("")


    const [showBookingBasicDialogSection, setShowBookingBasicDialogSection] = useState("basic")
    const [isBookingBasicDialogOpen, setIsBookingBasicDialogOpen] = useState(!isUpdateMode);

    const [openDelete, setOpenDelete] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [selectedTab, setSelectedTab] = useState(!isUpdateMode && 0);
    const [updateBookingTypeDB] = useMutation(UPDATE_BOOKING_TYPE);
    const [addBookingType] = useMutation(ADD_BOOKING_TYPE);
    const [additionalOptions, setAdditionalOptions] = useState(!!bookingType);
    const [updating, setUpdating] = useState(false);
    const [bookingApproval, setBookingApproval] = useState(
        bookingType?.bookingApproval ? bookingType?.bookingApproval : 0
    );

    const timeoutRef = useRef(null);
    const timeoutActiveRef = useRef(false);
    const notificationRef = useRef(null);


    const { canEditBookingType } = usePermissions();

    useEffect(() => {
        const canEdit = canEditBookingType(Number(bookingType?.createdBy));
        if (canEdit) {
            setIsDisabled(false)
        }
        else {
            setIsDisabled(true)
        }
    }, [canEditBookingType])

    useEffect(() => {
        if (updating && !timeoutActiveRef.current) {
            timeoutActiveRef.current = true;

            timeoutRef.current = setTimeout(() => {
                setUpdating(false);
                timeoutActiveRef.current = false;
            }, 2000);
        }

        return () => {
            clearTimeout(timeoutRef.current);
            timeoutActiveRef.current = false;
        };
    }, [updating]);

    useEffect(() => {
        if (notificationRef.current) {
            notificationRef.current.style.height = "0px";

            if (updating) {
                notificationRef.current.style.transition = "height 0.3s ease";
                notificationRef.current.style.height = "50px";
            } else {
                notificationRef.current.style.height = "0px";
            }
        }
    }, [updating]);


    useEffect(() => {
        if (businessBookingTypes?.getBusinessBookingTypes) {
            const foundBookingType = businessBookingTypes.getBusinessBookingTypes.find(
                (obj) => Number(obj.id) === Number(id)
            );
            if (foundBookingType) {
                setBookingType((prev) => (prev?.id === foundBookingType.id ? prev : foundBookingType));
            }
        }
    }, [businessBookingTypes, id]);
 
    

    if (loading) {
        return "Loading...";
    }

    if (error) {
        return "Error";
    }

    const handleBookingBasicOpenDialog = (showPart) => {
        setIsBookingBasicDialogOpen(true);
        showPart && setShowBookingBasicDialogSection(showPart)
    };

    const handleBookingBasicCloseDialog = () => {
        setIsBookingBasicDialogOpen(false);
    };

    const handleScheduleSettingsOpenDialog = (showPart) => {
        setIsScheduleSettings(true);
        showPart && setShowSection(showPart)
    };


    const handleInviteeQuestionCloseDialog = () => {
        setShowInviteeQuestions(false);
    };
    const handleScheduleSettingsCloseDialog = () => {
        setIsScheduleSettings(false);
    };
    const handleConfirmDelete = () => {
        deleteInviteeQuestion({ variables: { id: parseInt(currentId) } });
    };

    const scrollToSection = (ref) => {
        setTimeout(() => {
            const section2 = document.getElementById(ref);
            section2.scrollIntoView(true);
        }, 100);
    };


    const handleSubmit = async (submitProps) => {
        const updatedSubmitProps = {
            ...submitProps,
            businessId: Number(selectedBusiness.businessBasic.id),
        };

        if (bookingType) {
            updateBookingTypeDB({ variables: updatedSubmitProps })
                .then((res) => {
                    setUpdating(true);
                    dispatch(
                        updateBookingType(
                            Number(selectedBusiness.businessBasic.id),
                            res.data.updateBookingType,
                            updatedSubmitProps.assignedStaffs
                        )
                    );
                    refetch(); // ✅ Refresh data after update
                    updatedSubmitProps.DialogOpen(false);
                })
                .catch((error) => {
                    console.log(error);
                });
        } else {
            addBookingType({ variables: updatedSubmitProps })
                .then((res) => {
                    window.location.href = "/biz/update/" + res.data.addBookingType.id;
                    setBookingType(res.data.addBookingType);
                    setAdditionalOptions(true);
                    dispatch(
                        updateBookingType(
                            Number(selectedBusiness.businessBasic.id),
                            res.data.addBookingType,
                            submitProps.assignedStaffs
                        )
                    );
                    refetch(); // ✅ Refresh data after adding a new booking type
                    navigate('/biz/bookingtypes');
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };





    const combinedQuestions = [
        ...(bookingType?.customQuestions || []),
        ...(bookingType?.inviteeQuestions || [])
    ];

    const isNewBookingType = !id

    // Prevent rendering ONLY IF fetching an existing record and bookingType is still null
    if (!isNewBookingType && !bookingType) {
        return <div>Loading Booking Type...</div>;
    }



    const value = {
        props,
        isUpdateMode,
        handleScheduleSettingsOpenDialog,
        additionalOptions,
        selectedBusiness,
        bookingType: bookingType || {},
        errors,
        setErrors,
        setShowInviteeQuestions,
        showInviteeQuestions,
        isBookingBasicDialogOpen,
        handleBookingBasicOpenDialog,
        setIsBookingBasicDialogOpen,
        handleBookingBasicCloseDialog,
        showBookingBasicDialogSection,
        handleInviteeQuestionCloseDialog,
        isDisabled,
        combinedQuestions,
        notificationRef,
        CancelConfirmationModal,
        BookingTypeTime,
        showSection, 
        handleSubmit,
        scrollToSection,
        selectedTab,
        providerUser,
        updating,
        setUpdating,
        bookingApproval,
        setSelectedTab, 
        setIsDisabled,
        isScheduleSettings,
        setIsScheduleSettings, 
        ConfirmDialog,
        openDelete,
        setOpenDelete,
        handleConfirmDelete,
        handle: scrollToSection,
        onClose: handleScheduleSettingsCloseDialog,
        refetch,
        getBookingCategory
    };
    return (
        <BookingTypeContext.Provider value={value}>
            {children}
        </BookingTypeContext.Provider>
    );
};
export default BookingTypeProvider;
